import User from '../entity/User';
import Store from 'repatch';
import { History } from 'history';
import State from '../core/State';
import IntercomService from '../service/IntercomService';
import ErrorPresenter from '../service/ErrorPresenter';
import AuthUserWebApi, { MeNotFound } from '../service/persistent/AuthUserWebApi';
import { disposeUser, finishAuthentication, setSignedInUser, startAuthentication } from '../reducer/authentication';
import AuthenticationWebApi from '../service/persistent/AuthenticationWebApi';

class AuthenticationUsecase {
  private store: Store<State>;
  private history: History;
  private intercomService: IntercomService;
  private errorPresenter: ErrorPresenter;
  private authUserWebApi: AuthUserWebApi;
  private authenticationWebApi: AuthenticationWebApi;

  constructor({
    store,
    history,
    authenticationWebApi,
    intercomService,
    errorPresenter,
    authUserWebApi,
  }: {
    store: Store<State>;
    history: History;
    authenticationWebApi: AuthenticationWebApi;
    intercomService: IntercomService;
    errorPresenter: ErrorPresenter;
    authUserWebApi: AuthUserWebApi;
  }) {
    this.store = store;
    this.history = history;
    this.authenticationWebApi = authenticationWebApi;
    this.intercomService = intercomService;
    this.errorPresenter = errorPresenter;
    this.authUserWebApi = authUserWebApi;
  }
  public needSignedIn = (): void => {
    const user = this.store.getState().authentication.signedInUser;

    if (!user) {
      const err = new Error('message.require_signing');
      this.errorPresenter.showAsNonFatal(err);
      throw err;
    }
  };

  /**
   * サインアップ画面にリダイレクトさせる関数。すでにログインしている場合は profile のルートに遷移させる（サインアップの必要がないため）
   */
  public redirectKaizenIdforSignUp = (url: string): void => {
    const user = this.store.getState().authentication.signedInUser;
    user ? this.history.replace('/') : location.replace(url);
  };

  /**
   * logout APIを呼び出し、signoutする
   */
  public signOut = (): void => {
    const user = this.store.getState().authentication.signedInUser;

    if (!user) {
      throw new Error('It should not be possible to reach here.');
    }

    this.disposeToken();
    this.disposeUser();
    this.anonymizeExternalServices();
    this.history.push('/');
  };

  public signInFromPreviousSession = async (): Promise<void> => {
    this.store.dispatch(startAuthentication());

    try {
      await this.prepareUser();
    } catch (error) {
      this.authenticationFailed(error);
    } finally {
      this.store.dispatch(finishAuthentication());
    }

    const user = this.store.getState().authentication.signedInUser;
    if (!user) {
      this.disposeUser();
      this.store.dispatch(finishAuthentication());
      return;
    }
  };

  private prepareUser = async (): Promise<User> => {
    let user: User;

    try {
      user = await this.authUserWebApi.getMe();
    } catch (err) {
      if (err instanceof MeNotFound) {
        throw new UserNotFound();
      }
      throw err;
    }

    this.store.dispatch(setSignedInUser(user));
    this.identifyExternalServices(user);
    return user;
  };

  private disposeUser = () => {
    this.store.dispatch(disposeUser());
  };

  // APIを呼び出してtokenを無効化する
  private disposeToken = () => {
    this.authenticationWebApi.signOut();
  };

  private authenticationFailed = (err: Error, message = 'message.authentication_has_failed'): void => {
    if (err instanceof OrganizationNotFound) {
      message = 'message.no_available_organizations';
    }

    this.errorPresenter.showAsNonFatal(new Error(message));
  };

  private identifyExternalServices = (user: User): void => {
    // this.fullStoryService.identify(user);
    this.intercomService.identify(user);
  };

  private anonymizeExternalServices = (): void => {
    // this.fullStoryService.anonymize();
    this.intercomService.anonymize();
  };
}

class OrganizationNotFound extends Error {
  public readonly name = 'OrganizationNotFound';
  public readonly message = 'No available organization is found.';

  constructor() {
    super();
  }
}

class UserNotFound extends Error {
  public readonly name = 'UserNotFound';
  public readonly message = 'No available User is found.';

  constructor() {
    super();
  }
}

export default AuthenticationUsecase;
